import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Link } from 'react-router-dom';

import { toast } from 'react-toastify';

import { Container, Row, Col, Card, Form } from 'react-bootstrap';

import CustomNavbar from '../components/CustomNavbar';

import { confirmToast } from '../utils/toast';

import { clearRatingsCache } from '../hooks/useProductRatings';

/* Neutral fallback for products with no image (or a dead image URL) */
const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72">
      <rect width="72" height="72" rx="14" fill="#f3ede1"/>
      <text x="36" y="47" font-size="30" text-anchor="middle" fill="#c2410c">🛍</text>
    </svg>`
  );

/* One place where a rating bucket becomes a colour and an icon. Everything
   tinted on a card — top strip, score pill, thumbnail glow, hover — reads
   from here. */
const RATING_STATES = {
  Good: { icon: '⭐', from: '#15803d', to: '#4ade80', solid: '#22c55e' },
  Average: { icon: '😐', from: '#b45309', to: '#fcd34d', solid: '#f59e0b' },
  Poor: { icon: '⚠', from: '#991b1b', to: '#f87171', solid: '#ef4444' }
};

const getBucket = (rating) =>
  rating >= 4 ? 'Good' : rating >= 3 ? 'Average' : 'Poor';

const stateOf = (rating) => RATING_STATES[getBucket(rating)];

/* Rating buckets the admin acts on: anything under 3 stars needs the product
   looked at, 4+ is doing well. */
const FILTERS = [
  { key: 'All', label: '✨ All Products', color: '#9a3412' },
  { key: 'Poor', label: '⚠ Needs Attention (< 3★)', color: '#ef4444' },
  { key: 'Average', label: '😐 Average (3 – 3.9★)', color: '#f59e0b' },
  { key: 'Good', label: '⭐ Top Rated (4★ +)', color: '#22c55e' }
];

function AdminReviewsPage() {
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');

  const [ratingFilter, setRatingFilter] = useState('All');

  /* Product ids whose full review list is expanded */
  const [expanded, setExpanded] = useState([]);

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);

      setProducts(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const deleteReview = async (reviewId) => {
    const ok = await confirmToast('Delete this review permanently?', {
      confirmText: 'Delete',
      cancelText: 'Keep'
    });

    if (!ok) return;

    try {
      await axios.delete(
        `${API}/reviews/delete/${reviewId}`
      );

      toast.success('Review Deleted');

      /* Storefront cards cache the rating summary — invalidate it */
      clearRatingsCache();

      fetchReviews();
    } catch (error) {
      console.log(error);

      toast.error('Failed to delete review');
    }
  };

  const toggleExpanded = (productId) =>
    setExpanded((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );

  /* Filled/half/empty stars for an average like 3.6 */
  const renderStars = (rating) => {
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= Math.round(rating) ? 'arv-star-on' : ''}>
          ★
        </span>
      );
    }

    return <span className="arv-stars">{stars}</span>;
  };

  if (loading) {
    return (
      <>
        <CustomNavbar />

        <div className="arv-wrap">
          <Container className="text-center py-5">
            <div className="arv-loader" aria-hidden="true">
              <span className="arv-loader-ring" />
              <span className="arv-loader-star">⭐</span>
            </div>
            <p className="arv-loader-text">Gathering customer reviews…</p>
          </Container>
        </div>

        <style>{reviewsStyles}</style>
      </>
    );
  }

  const totalReviews = products.reduce((sum, p) => sum + p.totalReviews, 0);

  const needsAttention = products.filter(
    (p) => getBucket(p.averageRating) === 'Poor'
  ).length;

  /* Weighted across every review, not a mean of means */
  const overallAverage = totalReviews
    ? products.reduce((sum, p) => sum + p.averageRating * p.totalReviews, 0) /
      totalReviews
    : 0;

  const countOf = (key) =>
    key === 'All'
      ? products.length
      : products.filter((p) => getBucket(p.averageRating) === key).length;

  const filteredProducts = products.filter((product) => {
    const keyword = search.toLowerCase();

    const matchesSearch =
      !keyword ||
      product.name?.toLowerCase().includes(keyword) ||
      product.category?.toLowerCase().includes(keyword) ||
      product.reviews?.some(
        (review) =>
          review.userName?.toLowerCase().includes(keyword) ||
          review.comment?.toLowerCase().includes(keyword)
      );

    const matchesRating =
      ratingFilter === 'All' || getBucket(product.averageRating) === ratingFilter;

    return matchesSearch && matchesRating;
  });

  return (
    <>
      <CustomNavbar />

      <div className="arv-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 arv-hero">
            <span className="arv-hero-aurora" aria-hidden="true" />
            <span className="arv-hero-blob arv-hero-blob-1" />
            <span className="arv-hero-blob arv-hero-blob-2" />

            {/* Motes drifting up through the banner */}
            <span className="arv-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="arv-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative p-4 p-md-5 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h1 className="fw-bold mb-1 arv-hero-title">
                  <span className="arv-hero-icon">⭐</span>
                  <span className="arv-hero-title-text">Review Management</span>
                </h1>
                <p className="mb-0 arv-hero-text">
                  See what customers say about each product and update the ones
                  letting you down
                </p>
              </div>

              <div className="arv-stats">
                <div className="arv-stat is-score">
                  <span className="arv-stat-value">
                    {overallAverage.toFixed(1)}★
                  </span>
                  <span className="arv-stat-label">Average</span>
                </div>

                <div className="arv-stat">
                  <span className="arv-stat-value">{totalReviews}</span>
                  <span className="arv-stat-label">Reviews</span>
                </div>

                <div className="arv-stat">
                  <span className="arv-stat-value">{products.length}</span>
                  <span className="arv-stat-label">Products</span>
                </div>

                {needsAttention > 0 && (
                  <div className="arv-stat is-alert">
                    <span className="arv-stat-value">{needsAttention}</span>
                    <span className="arv-stat-label">Need help</span>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Search */}
          <div className="arv-search-row">
            <div className="arv-search">
              <span className="arv-search-icon" aria-hidden="true">
                🔍
              </span>

              <Form.Control
                className="arv-search-input"
                placeholder="Search by product, category, reviewer or comment..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button
                  type="button"
                  className="arv-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  ×
                </button>
              )}
            </div>

            <span className="arv-result-count">
              <span className="arv-result-num">{filteredProducts.length}</span>
              shown
            </span>
          </div>

          {/* Rating filters */}
          <div className="arv-chips">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`arv-chip${ratingFilter === f.key ? ' is-active' : ''}`}
                style={{ '--chip': f.color }}
                onClick={() => setRatingFilter(f.key)}
              >
                {f.label}
                <span className="arv-chip-count">{countOf(f.key)}</span>
              </button>
            ))}
          </div>

          {/* Product review cards */}
          {filteredProducts.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '18px' }}
            >
              <Card.Body>
                <div className="arv-empty-icon">📝</div>
                <h5 className="fw-bold mt-2">No Reviews Found</h5>
                <p className="text-muted mb-0">
                  {products.length === 0
                    ? 'No customer has reviewed a product yet.'
                    : 'Try a different search or filter.'}
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {filteredProducts.map((product, index) => {
                const isOpen = expanded.includes(product.productId);

                const visibleReviews = isOpen
                  ? product.reviews
                  : product.reviews.slice(0, 2);

                const state = stateOf(product.averageRating);
                const bucket = getBucket(product.averageRating);

                return (
                  <Col xl={4} lg={6} md={6} xs={12} key={product.productId}>
                    <Card
                      className={`border-0 shadow-sm h-100 arv-card is-${bucket.toLowerCase()}`}
                      style={{
                        '--v-from': state.from,
                        '--v-to': state.to,
                        '--v-accent': state.solid,
                        /* capped so late cards don't wait seconds */
                        '--i': Math.min(index, 9)
                      }}
                    >
                      <span className="arv-card-strip" aria-hidden="true" />

                      <Card.Body className="d-flex flex-column p-3">
                        {/* Product header */}
                        <div className="d-flex gap-2 mb-2">
                          <span className="arv-thumb-wrap">
                            <img
                              src={product.image || PLACEHOLDER_IMG}
                              alt={product.name}
                              className="arv-thumb"
                              onError={(e) => {
                                e.currentTarget.src = PLACEHOLDER_IMG;
                              }}
                            />
                          </span>

                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <h5 className="fw-bold mb-1 text-truncate arv-name">
                                {product.name}
                              </h5>

                              <span className="arv-score">
                                {product.averageRating.toFixed(1)} ★
                              </span>
                            </div>

                            <div className="d-flex align-items-center gap-2 mb-1">
                              {renderStars(product.averageRating)}
                              <small className="text-muted">
                                {product.totalReviews} review
                                {product.totalReviews > 1 ? 's' : ''}
                              </small>
                            </div>

                            <small className="text-muted d-block">
                              {product.exists ? (
                                <>
                                  ₹{product.price} · Stock: {product.stock}
                                  {product.category
                                    ? ` · ${product.category}`
                                    : ''}
                                </>
                              ) : (
                                <span className="arv-gone">
                                  ⚠ This product no longer exists
                                </span>
                              )}
                            </small>
                          </div>
                        </div>

                        {/* Rating breakdown */}
                        <div className="arv-detail mb-2">
                          <div className="arv-label mb-1">
                            Rating Breakdown
                          </div>

                          {[5, 4, 3, 2, 1].map((star) => {
                            const count = product.ratingCounts[star] || 0;

                            const percent = product.totalReviews
                              ? (count / product.totalReviews) * 100
                              : 0;

                            return (
                              <div
                                className="d-flex align-items-center gap-2 arv-bar-row"
                                key={star}
                              >
                                <small className="arv-bar-star">{star}★</small>

                                <div className="arv-bar-track">
                                  <div
                                    className={`arv-bar-fill ${
                                      star >= 4
                                        ? 'is-good'
                                        : star === 3
                                          ? 'is-mid'
                                          : 'is-bad'
                                    }`}
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>

                                <small className="arv-bar-count">{count}</small>
                              </div>
                            );
                          })}
                        </div>

                        {/* Low-rating nudge — the reason this page exists */}
                        {bucket === 'Poor' && (
                          <div className="arv-alert mb-2">
                            <span className="arv-alert-ico" aria-hidden="true">
                              ⚠
                            </span>
                            Rated poorly — check the comments and fix the
                            listing.
                          </div>
                        )}

                        {/* Reviews */}
                        <div className="arv-label mb-1">Customer Reviews</div>

                        <div className="d-flex flex-column gap-2 mb-2">
                          {visibleReviews.map((review) => (
                            <div
                              className={`arv-review is-${getBucket(
                                review.rating
                              ).toLowerCase()}`}
                              key={review._id}
                            >
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <div className="d-flex align-items-center gap-2 min-w-0">
                                  <span className="arv-avatar">
                                    {review.userName?.charAt(0).toUpperCase() ||
                                      '?'}
                                  </span>

                                  <div className="min-w-0">
                                    <div className="fw-semibold text-truncate">
                                      {review.userName}
                                    </div>
                                    <small className="text-muted">
                                      {new Date(
                                        review.createdAt
                                      ).toLocaleDateString()}
                                    </small>
                                  </div>
                                </div>

                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                  {renderStars(review.rating)}

                                  <button
                                    type="button"
                                    className="arv-del"
                                    title="Delete this review"
                                    onClick={() => deleteReview(review._id)}
                                  >
                                    🗑
                                  </button>
                                </div>
                              </div>

                              <p className="mb-0 mt-2 arv-comment">
                                {review.comment}
                              </p>
                            </div>
                          ))}
                        </div>

                        {product.reviews.length > 2 && (
                          <button
                            type="button"
                            className="arv-more"
                            onClick={() => toggleExpanded(product.productId)}
                          >
                            <span
                              className={`arv-more-caret${isOpen ? ' is-open' : ''}`}
                              aria-hidden="true"
                            >
                              ▼
                            </span>
                            {isOpen
                              ? 'Show less'
                              : `Show all ${product.reviews.length} reviews`}
                          </button>
                        )}

                        {/* Actions — act on what the reviews are telling you */}
                        <div className="d-flex gap-2 mt-auto pt-2">
                          {product.exists ? (
                            <>
                              <Link
                                to={`/admin/edit-product/${product.productId}`}
                                className="arv-act arv-act-edit flex-fill"
                              >
                                <span
                                  className="arv-act-ico"
                                  aria-hidden="true"
                                >
                                  ✏
                                </span>
                                Edit Product
                              </Link>

                              <Link
                                to={`/product/${product.productId}`}
                                className="arv-act arv-act-view flex-fill"
                              >
                                <span
                                  className="arv-act-ico"
                                  aria-hidden="true"
                                >
                                  👁
                                </span>
                                View
                              </Link>
                            </>
                          ) : (
                            /* A deleted product has nowhere to link to — a
                               disabled <Link> still navigates, so render plain
                               text instead of a dead anchor. */
                            <span className="arv-act arv-act-dead flex-fill">
                              🚫 Product deleted — reviews are orphaned
                            </span>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{reviewsStyles}</style>
    </>
  );
}

const reviewsStyles = `
  .arv-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 48px) 0;
  }

  /* ===== Loading ===== */
  .arv-loader {
    position: relative;
    width: 88px;
    height: 88px;
    margin: 40px auto 0;
  }
  .arv-loader-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 3px solid rgba(245, 158, 11, 0.22);
    border-top-color: #ea580c;
    border-right-color: #f59e0b;
    animation: arvSpin 1s linear infinite;
  }
  @keyframes arvSpin {
    to { transform: rotate(360deg); }
  }
  .arv-loader-star {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    animation: arvIconBob 2.4s ease-in-out infinite;
  }
  .arv-loader-text {
    margin-top: 18px;
    font-weight: 700;
    color: #9a3412;
  }

  /* ===== Sanctum banner ===== */
  .arv-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 22px;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #3b0764 30%, #7c2d12 64%, #ea580c);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.25) !important;
    animation: arvHeroDrift 18s ease-in-out infinite alternate;
  }
  @keyframes arvHeroDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .arv-hero::before,
  .arv-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: arvThread 3.6s linear infinite;
  }
  .arv-hero::before { top: 0; }
  .arv-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes arvThread {
    to { background-position: 220% 0; }
  }

  .arv-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.42), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(168,85,247,0.34), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(234,88,12,0.38), transparent 62%);
    filter: blur(30px);
    animation: arvAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes arvAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .arv-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
    z-index: 0;
  }
  .arv-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 20%;
  }
  .arv-hero-blob-2 {
    width: 180px; height: 180px;
    background: #a855f7;
    bottom: -90px; right: 8%;
    opacity: 0.35;
  }

  .arv-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .arv-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: arvMote 12s linear infinite;
  }
  .arv-mote:nth-child(1) { left: 7%;  animation-delay: 0s; }
  .arv-mote:nth-child(2) { left: 20%; animation-delay: 4.3s; width: 4px; height: 4px; }
  .arv-mote:nth-child(3) { left: 33%; animation-delay: 1.7s; }
  .arv-mote:nth-child(4) { left: 46%; animation-delay: 7.2s; width: 5px; height: 5px; }
  .arv-mote:nth-child(5) { left: 59%; animation-delay: 2.8s; }
  .arv-mote:nth-child(6) { left: 72%; animation-delay: 9.2s; width: 4px; height: 4px; }
  .arv-mote:nth-child(7) { left: 85%; animation-delay: 5.6s; }
  .arv-mote:nth-child(8) { left: 95%; animation-delay: 3.4s; width: 5px; height: 5px; }
  @keyframes arvMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-230px) translateX(22px) scale(1.1); }
  }

  .arv-hero .card-body { z-index: 2; }
  .arv-hero-title { display: flex; align-items: center; gap: 10px; }
  .arv-hero-icon {
    display: inline-block;
    animation: arvIconBob 3.2s ease-in-out infinite;
  }
  @keyframes arvIconBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(-8deg); }
  }
  .arv-hero-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: arvTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes arvTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .arv-hero-text { opacity: 0.85; max-width: 520px; }

  .arv-stats { display: flex; flex-wrap: wrap; gap: 9px; }
  .arv-stat {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 82px;
    padding: 9px 15px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(253, 230, 138, 0.3);
    backdrop-filter: blur(6px);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, border-color 0.25s ease;
  }
  .arv-stat:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(253, 230, 138, 0.65);
  }
  .arv-stat-value { font-size: 1.3rem; font-weight: 800; line-height: 1.1; }
  .arv-stat-label {
    font-size: 0.63rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.78;
    margin-top: 2px;
  }
  .arv-stat.is-score .arv-stat-value { color: #fde68a; }
  .arv-stat.is-alert .arv-stat-value { color: #fca5a5; }
  /* Products losing customers are the ones to look at */
  .arv-stat.is-alert { animation: arvAlert 2.4s ease-in-out infinite; }
  @keyframes arvAlert {
    0%, 100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.45); }
    50%      { box-shadow: 0 0 0 7px rgba(248, 113, 113, 0); }
  }

  /* ===== Search ===== */
  .arv-search-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }
  .arv-search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 340px;
    max-width: 560px;
    padding: 4px 14px;
    border-radius: 50px;
    background: #fff;
    border: 2px solid rgba(245, 158, 11, 0.26);
    box-shadow: 0 10px 28px rgba(194, 65, 12, 0.1);
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .arv-search:focus-within {
    border-color: #f59e0b;
    box-shadow: 0 14px 36px rgba(245, 158, 11, 0.28);
  }
  .arv-search-icon { flex: none; font-size: 1rem; }
  .arv-search-input {
    border: none !important;
    padding: 9px 0;
    box-shadow: none !important;
    background: transparent;
  }
  .arv-search-clear {
    flex: none;
    border: none;
    width: 22px; height: 22px;
    border-radius: 50%;
    line-height: 1;
    font-size: 1.05rem;
    color: #b45309;
    background: #fff7ed;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .arv-search-clear:hover { background: #fde68a; transform: rotate(90deg); }
  .arv-result-count {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #9a3412;
  }
  .arv-result-num {
    font-weight: 800;
    color: #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    border-radius: 30px;
    padding: 2px 11px;
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }

  /* ===== Rating chips ===== */
  .arv-chips { display: flex; flex-wrap: wrap; gap: 9px; margin-bottom: 22px; }
  .arv-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 9px 7px 15px;
    border-radius: 50px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid var(--chip, #f59e0b);
    color: var(--chip, #9a3412);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
  }
  .arv-chip:hover { transform: translateY(-2px); }
  .arv-chip:active { transform: translateY(0) scale(0.96); }
  .arv-chip.is-active {
    color: #fff;
    background: var(--chip, #ea580c);
    animation: arvChipPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes arvChipPop {
    0%   { transform: scale(0.88); }
    55%  { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
  .arv-chip-count {
    min-width: 22px;
    padding: 1px 8px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 800;
    background: #fff7ed;
    color: var(--chip, #9a3412);
  }
  .arv-chip.is-active .arv-chip-count { background: rgba(255, 255, 255, 0.92); }

  /* ===== Product cards ===== */
  .arv-card {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(245, 158, 11, 0.14) !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: arvCardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 65ms);
  }
  @keyframes arvCardIn {
    from { opacity: 0; transform: translateY(20px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  /* The old 5px inline border, but alive */
  .arv-card-strip {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: linear-gradient(90deg,
      var(--v-from, #ea580c), var(--v-to, #f59e0b), var(--v-from, #ea580c));
    background-size: 220% 100%;
    animation: arvStrip 3.4s linear infinite;
  }
  @keyframes arvStrip {
    to { background-position: 220% 0; }
  }
  .arv-card:hover {
    transform: translateY(-7px);
    box-shadow: 0 24px 42px color-mix(in srgb, var(--v-accent, #ea580c) 26%, transparent) !important;
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .arv-card:hover { box-shadow: 0 22px 40px rgba(194, 65, 12, 0.18) !important; }
  }
  /* A product customers dislike keeps a faint red wash */
  .arv-card.is-poor { background: linear-gradient(180deg, #fffafa, #ffffff 40%); }

  .arv-thumb-wrap {
    flex-shrink: 0;
    display: inline-block;
    line-height: 0;
    border-radius: 14px;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.3s ease;
  }
  .arv-card:hover .arv-thumb-wrap {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 10px 22px color-mix(in srgb, var(--v-accent, #ea580c) 38%, transparent);
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .arv-card:hover .arv-thumb-wrap { box-shadow: 0 10px 22px rgba(124, 45, 18, 0.25); }
  }
  .arv-thumb {
    width: 54px;
    height: 54px;
    object-fit: cover;
    border-radius: 12px;
    background: #faf5ff;
    border: 1px solid #f1e4d0;
  }
  .min-w-0 { min-width: 0; }

  .arv-name {
    font-size: 0.98rem;
    color: #1f2937;
    transition: color 0.25s ease;
  }
  .arv-card:hover .arv-name { color: var(--v-accent, #ea580c); }

  /* Average score pill, tinted by the bucket */
  .arv-score {
    flex-shrink: 0;
    padding: 2px 10px;
    border-radius: 30px;
    font-size: 0.73rem;
    font-weight: 800;
    white-space: nowrap;
    color: #fff;
    background: linear-gradient(135deg, var(--v-from, #ea580c), var(--v-to, #f59e0b));
    box-shadow: 0 4px 12px color-mix(in srgb, var(--v-accent, #ea580c) 38%, transparent);
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .arv-score { box-shadow: 0 4px 12px rgba(124, 45, 18, 0.25); }
  }

  .arv-gone {
    color: #b91c1c;
    font-weight: 700;
  }

  .arv-detail {
    background: #fffdf7;
    border: 1px solid #f6eee2;
    border-radius: 10px;
    padding: 8px 10px;
    transition: background 0.25s ease;
  }
  .arv-card:hover .arv-detail { background: #fff; }
  .arv-label {
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #b45309;
  }

  /* ===== Stars ===== */
  .arv-stars {
    color: #e2ddd4;
    font-size: 0.82rem;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
  .arv-stars .arv-star-on { color: #f59e0b; }
  /* Only a top-rated product gets the gold shimmer */
  .arv-card.is-good .arv-stars .arv-star-on {
    animation: arvTwinkle 2.6s ease-in-out infinite;
  }
  @keyframes arvTwinkle {
    0%, 100% { text-shadow: none; }
    50%      { text-shadow: 0 0 10px rgba(251, 191, 36, 0.9); }
  }

  /* ===== Rating distribution bars ===== */
  .arv-bar-row + .arv-bar-row { margin-top: 3px; }
  .arv-bar-star {
    width: 22px;
    font-size: 0.7rem;
    color: #78716c;
    font-weight: 700;
    flex-shrink: 0;
  }
  .arv-bar-track {
    flex: 1 1 auto;
    height: 6px;
    border-radius: 8px;
    background: #efeae1;
    overflow: hidden;
    box-shadow: inset 0 1px 3px rgba(124, 45, 18, 0.1);
  }
  .arv-bar-fill {
    height: 100%;
    border-radius: 8px;
    transform-origin: left center;
    transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    animation: arvBarGrow 0.7s cubic-bezier(0.22, 1, 0.36, 1) backwards;
  }
  @keyframes arvBarGrow {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
  .arv-bar-fill.is-good { background: linear-gradient(90deg, #15803d, #4ade80); }
  .arv-bar-fill.is-mid  { background: linear-gradient(90deg, #b45309, #fbbf24); }
  .arv-bar-fill.is-bad  { background: linear-gradient(90deg, #991b1b, #f87171); }
  .arv-bar-count {
    width: 20px;
    font-size: 0.7rem;
    text-align: right;
    color: #78716c;
    flex-shrink: 0;
  }

  /* ===== Low rating warning ===== */
  .arv-alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    border-radius: 10px;
    padding: 7px 11px;
    font-size: 0.78rem;
    font-weight: 500;
    line-height: 1.4;
  }
  .arv-alert-ico {
    flex: none;
    font-size: 1rem;
    animation: arvPulse 1.8s ease-in-out infinite;
  }
  @keyframes arvPulse {
    0%, 100% { transform: scale(1);    opacity: 0.75; }
    50%      { transform: scale(1.25); opacity: 1; }
  }

  /* ===== Individual review ===== */
  .arv-review {
    position: relative;
    background: #fff;
    border: 1px solid #f1e4d0;
    border-left: 3px solid #e2ddd4;
    border-radius: 10px;
    padding: 8px 10px;
    transition: transform 0.25s ease, box-shadow 0.25s ease,
      border-color 0.25s ease;
  }
  /* Each review's own rating tints its edge */
  .arv-review.is-good { border-left-color: #22c55e; }
  .arv-review.is-average { border-left-color: #f59e0b; }
  .arv-review.is-poor { border-left-color: #ef4444; }
  .arv-review:hover {
    transform: translateX(3px);
    box-shadow: 0 8px 18px rgba(124, 45, 18, 0.1);
  }
  .arv-avatar {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-weight: 700;
    font-size: 0.78rem;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
  }
  .arv-review .fw-semibold { font-size: 0.85rem; }
  .arv-review small { font-size: 0.7rem; }
  /* Long comments would blow the card height out — cap them */
  .arv-comment {
    font-size: 0.8rem;
    line-height: 1.45;
    color: #475569;
    white-space: pre-wrap;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .arv-del {
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 2px 7px;
    line-height: 1.3;
    font-size: 0.75rem;
    color: #b91c1c;
    background: #fff;
    cursor: pointer;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, color 0.25s ease, border-color 0.25s ease,
      box-shadow 0.25s ease;
  }
  .arv-del:hover {
    transform: translateY(-2px);
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 8px 18px rgba(220, 38, 38, 0.4);
  }
  .arv-del:active { transform: translateY(0) scale(0.94); }

  /* ===== Show more ===== */
  .arv-more {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    background: none;
    padding: 0;
    margin-bottom: 2px;
    font-size: 0.78rem;
    font-weight: 700;
    color: #c2410c;
    cursor: pointer;
    transition: color 0.2s ease, transform 0.2s ease;
  }
  .arv-more:hover { color: #ea580c; transform: translateX(2px); }
  .arv-more-caret {
    display: inline-block;
    font-size: 0.7rem;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .arv-more-caret.is-open { transform: rotate(180deg); }

  /* ===== Card actions ===== */
  .arv-act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border: none;
    border-radius: 10px;
    padding: 7px 10px;
    font-size: 0.78rem;
    font-weight: 700;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, filter 0.25s ease, background 0.25s ease,
      color 0.25s ease, border-color 0.25s ease;
  }
  .arv-act:hover { transform: translateY(-2px); }
  .arv-act:active { transform: translateY(0) scale(0.96); }
  .arv-act-ico { display: inline-block; }

  .arv-act-edit {
    color: #431407;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.35);
  }
  .arv-act-edit:hover {
    color: #431407;
    filter: brightness(1.06);
    box-shadow: 0 12px 26px rgba(245, 158, 11, 0.5);
  }
  .arv-act-edit:hover .arv-act-ico {
    animation: arvWiggle 0.6s ease-in-out infinite;
  }
  @keyframes arvWiggle {
    0%, 100% { transform: rotate(0deg); }
    32%      { transform: rotate(-17deg); }
    66%      { transform: rotate(13deg); }
  }

  .arv-act-view {
    border: 1px solid #d6d3d1;
    color: #57534e;
    background: #fff;
  }
  .arv-act-view:hover {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #3b0764, #7c3aed 55%, #a855f7);
    box-shadow: 0 10px 22px rgba(124, 58, 237, 0.4);
  }

  /* Product gone — a statement, not a button */
  .arv-act-dead {
    cursor: default;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px dashed #fecaca;
    font-weight: 600;
  }
  .arv-act-dead:hover { transform: none; }

  .arv-empty-icon {
    font-size: 3rem;
    animation: arvIconBob 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .arv-hero,
    .arv-hero::before,
    .arv-hero::after,
    .arv-hero-aurora,
    .arv-mote,
    .arv-hero-icon,
    .arv-hero-title-text,
    .arv-stat.is-alert,
    .arv-chip.is-active,
    .arv-card,
    .arv-card-strip,
    .arv-card.is-good .arv-stars .arv-star-on,
    .arv-bar-fill,
    .arv-alert-ico,
    .arv-act-edit:hover .arv-act-ico,
    .arv-loader-star,
    .arv-empty-icon { animation: none !important; }
    .arv-motes { display: none; }
    .arv-hero-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
`;

export default AdminReviewsPage;
